/** Módulos CMS y plantillas de rol (RBAC). Mismo orden que el menú lateral. */
import {
  LayoutDashboard,
  Home,
  Users,
  MessageSquare,
  MessageCircleWarning,
  Link2,
  Menu,
  BookOpen,
  Award,
  Briefcase,
  FlaskConical,
  Beaker,
  Palette,
  Images,
  UserCog,
  Calculator,
  BarChart3,
  Shield,
} from "lucide-react";

/** Pantallas que antes iban agrupadas en `content`. */
export const CMS_CONTENT_MODULE_IDS = [
  "home",
  "footer",
  "navigation",
  "about",
  "credibility",
  "services",
  "research",
  "labsyso",
  "identidad",
  "icons",
  "calculadoras",
  "privacidad",
];

export const CMS_MODULES = [
  { id: "dashboard", label: "Resumen", Icon: LayoutDashboard },
  { id: "home", label: "Home", Icon: Home },
  { id: "clientes", label: "Clientes", Icon: Users },
  { id: "leads", label: "Consultas / cotizaciones", Icon: MessageSquare },
  { id: "quejas", label: "Quejas / sugerencias", Icon: MessageCircleWarning },
  { id: "footer", label: "Footer", Icon: Link2 },
  { id: "navigation", label: "Menú responsivo", Icon: Menu },
  { id: "about", label: "Nosotros", Icon: BookOpen },
  { id: "credibility", label: "Credibilidad", Icon: Award },
  { id: "services", label: "Servicios", Icon: Briefcase },
  { id: "research", label: "Investigación", Icon: FlaskConical },
  { id: "labsyso", label: "LABSYSO", Icon: Beaker },
  { id: "identidad", label: "Identidad", Icon: Palette },
  { id: "icons", label: "Iconos", Icon: Images },
  { id: "calculadoras", label: "Calculadoras", Icon: Calculator },
  { id: "privacidad", label: "Privacidad", Icon: Shield },
  { id: "informes", label: "Informes", Icon: BarChart3 },
  { id: "users", label: "Usuarios", Icon: UserCog },
];

export const CMS_MODULE_IDS = CMS_MODULES.map((m) => m.id);

/** owner > admin > editor / vendedor / calidad */
export const CMS_ROLES = ["owner", "admin", "editor", "vendedor", "calidad", "medico"];

/** Roles que pueden abrir el panel Usuarios (vía Functions). */
export const CMS_USER_MANAGER_ROLES = ["owner", "admin"];

const CONTENT_PACK = [...CMS_CONTENT_MODULE_IDS];

/** Plantillas por rol (owner/admin pueden sobrescribir casillas salvo reglas de UI). */
export const ROLE_MODULE_TEMPLATES = {
  owner: [...CMS_MODULE_IDS],
  admin: ["dashboard", ...CONTENT_PACK, "clientes", "leads", "quejas", "informes", "users"],
  editor: ["dashboard", ...CONTENT_PACK, "clientes", "leads", "quejas"],
  vendedor: ["dashboard", "clientes", "leads"],
  calidad: ["dashboard", "quejas"],
  medico: [],
};

function expandLegacyContent(list) {
  const set = new Set(
    (Array.isArray(list) ? list : []).map((x) => String(x || "").trim()).filter(Boolean)
  );
  if (set.has("content")) {
    CMS_CONTENT_MODULE_IDS.forEach((id) => set.add(id));
    set.delete("content");
  }
  return set;
}

export function sanitizeModules(list) {
  const set = expandLegacyContent(list);
  return CMS_MODULE_IDS.filter((id) => set.has(id));
}

/** Módulos que vienen con el rol (plantilla viva del catálogo). */
export function roleDefaultModules(role, catalog) {
  if (role === "owner") return [...CMS_MODULE_IDS];
  if (role === "medico") return [];
  const def = Array.isArray(catalog) ? catalog.find((r) => r.id === role) : null;
  if (def && Array.isArray(def.modules)) {
    const fromCatalog = sanitizeModules(def.modules);
    if (role === "admin") {
      if (!fromCatalog.includes("users")) fromCatalog.push("users");
      if (!fromCatalog.includes("informes")) fromCatalog.push("informes");
    }
    return fromCatalog;
  }
  return [...(ROLE_MODULE_TEMPLATES[role] || ROLE_MODULE_TEMPLATES.editor)];
}

/** Pantallas que no trae el rol y que owner/admin pueden autorizar a un usuario. */
export function additionalModuleIds(role, catalog) {
  if (role === "owner" || role === "medico") return [];
  const defaults = new Set(roleDefaultModules(role, catalog));
  return CMS_MODULE_IDS.filter((id) => {
    if (defaults.has(id)) return false;
    if (id === "users" || id === "informes") return false;
    return true;
  });
}

export function inferModuleExtras(role, modules, catalog) {
  const allowed = new Set(additionalModuleIds(role, catalog));
  return sanitizeModules(modules).filter((id) => allowed.has(id));
}

/** Lista efectiva: plantilla del rol ∪ extras (users queda en admin/owner). */
export function mergeRoleAndExtras(role, extras, catalog) {
  if (role === "owner") return [...CMS_MODULE_IDS];
  if (role === "medico") return [];
  const base = roleDefaultModules(role, catalog);
  const allowedExtra = new Set(additionalModuleIds(role, catalog));
  const extra = sanitizeModules(extras).filter((id) => allowedExtra.has(id));
  const next = [...base];
  for (const id of extra) {
    if (!next.includes(id)) next.push(id);
  }
  if (role === "admin") {
    if (!next.includes("users")) next.push("users");
    if (!next.includes("informes")) next.push("informes");
  }
  return next;
}

export function modulesForRole(role, override, catalog) {
  if (role === "owner") return [...CMS_MODULE_IDS];
  if (role === "medico") return [];
  if (Array.isArray(override) && override.length) {
    const next = mergeRoleAndExtras(role, inferModuleExtras(role, override, catalog), catalog);
    if (role === "admin") {
      if (!next.includes("users")) next.push("users");
      if (!next.includes("informes")) next.push("informes");
    }
    return next;
  }
  return roleDefaultModules(role, catalog);
}

export function resolveUserModules(profile, catalog) {
  if (!profile || profile.status !== "active") return [];
  if (profile.role === "owner") return [...CMS_MODULE_IDS];
  if (profile.role === "medico") return [];
  const extras = Array.isArray(profile.moduleExtras) ? profile.moduleExtras : [];
  return mergeRoleAndExtras(profile.role, extras, catalog);
}

export function userHasModule(profile, moduleId, catalog) {
  return resolveUserModules(profile, catalog).includes(moduleId);
}

export function canManageCmsUsers(profile) {
  return (
    !!profile &&
    profile.status === "active" &&
    CMS_USER_MANAGER_ROLES.includes(profile.role) &&
    userHasModule(profile, "users")
  );
}
